/**
 * One status language for every stream. Each stream keeps its own steps
 * ("QA Passed", "First Draft Them"); this is what everyone else sees.
 */
export const PLAIN_STATUSES = ['not_started', 'in_progress', 'waiting', 'on_hold', 'done', 'stopped'] as const;

export type PlainStatus = (typeof PLAIN_STATUSES)[number];

export const PLAIN_STATUS_LABEL: Record<PlainStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  waiting: 'Blocked',
  on_hold: 'On hold',
  done: 'Done',
  stopped: 'Stopped',
};

/** Pill classes: grey, blue, red, green, light grey. */
export const PLAIN_STATUS_PILL: Record<PlainStatus, string> = {
  not_started: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  in_progress: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200',
  waiting: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200',
  on_hold: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-300',
  done: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200',
  stopped: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
};

export const PLAIN_STATUS_DOT: Record<PlainStatus, string> = {
  not_started: 'bg-slate-400',
  in_progress: 'bg-blue-600',
  waiting: 'bg-red-500',
  on_hold: 'bg-zinc-400',
  done: 'bg-emerald-600',
  stopped: 'bg-zinc-300',
};

const ON_HOLD_NAME = /\b(on hold|hold|paused|parked)\b/i;

export type PlainStatusInput = {
  group?: string | null;
  name?: string | null;
  /** Set while the item is blocked on someone or something. */
  blocked?: boolean;
  /** A launch step its stream has marked live. */
  live?: boolean;
};

export function plainStatus({ group, name, blocked = false, live = false }: PlainStatusInput): PlainStatus {
  if (live || group === 'completed') return 'done';
  if (group === 'cancelled') return 'stopped';
  if (blocked) return 'waiting';
  if (name && ON_HOLD_NAME.test(name)) return 'on_hold';
  if (group === 'started') return 'in_progress';
  return 'not_started';
}

/** "Live" reads better than "Done" for launch steps and releases. */
export function plainStatusLabel(status: PlainStatus, options: { live?: boolean } = {}): string {
  if (status === 'done' && options.live) return 'Live';
  return PLAIN_STATUS_LABEL[status];
}

/** The stream's own step, shown under the plain label when it adds something. */
export function stepDetail(status: PlainStatus, stateName: string | null | undefined): string | null {
  const name = stateName?.trim();
  if (!name) return null;
  const plain = PLAIN_STATUS_LABEL[status].toLowerCase();
  const lower = name.toLowerCase();
  if (lower === plain) return null;
  if (status === 'done' && ['done', 'completed', 'complete'].includes(lower)) return null;
  if (status === 'stopped' && ['cancelled', 'canceled'].includes(lower)) return null;
  if (status === 'not_started' && ['backlog', 'todo', 'to do'].includes(lower)) return null;
  return name;
}
